const { translateString } = window.BAO.Utils

class TagalysSearch extends window.BAO.CustomElement() {
  connectedCallback () {
    super.connectedCallback()
  }

  setupListeners () {
    super.setupListeners()

    if (!window?.onTagalysReady) return

    this._initialized = false

    this.currentPage = 0
    this.totalPages = 0
    this.itemsPerPage = 48

    this.isLoadingPage = false
    this.isRestoringFromUrl = false
    this.targetPageFromUrl = 1

    this.totalResultsCount = 0
    this.renderedResultsCount = 0

    this.handleInitialise = this.handleInitialise.bind(this)
    this.handleLoadMoreClick = this.handleLoadMoreClick.bind(this)
    this.updateLoadMoreButton = this.updateLoadMoreButton.bind(this)
    this.updatePaginationMeta = this.updatePaginationMeta.bind(this)
    this.renderProductsHtml = this.renderProductsHtml.bind(this)
    this.buildHeader = this.buildHeader.bind(this)
    this.handleFilterTrigger = this.handleFilterTrigger.bind(this)
    this.handleFilterChange = this.handleFilterChange.bind(this)
    this.setFilterTotal = this.setFilterTotal.bind(this)
    this.handleSortChange = this.handleSortChange.bind(this)
    this.handleActiveTagClick = this.handleActiveTagClick.bind(this)
    this.buildActiveFiltersFromApplied =
      this.buildActiveFiltersFromApplied.bind(this)

    this.listeners.add(
      this.els['load-more']?.element,
      'click',
      this.handleLoadMoreClick,
    )
    this.listeners.add(
      this.els['header-trigger']?.element,
      'click',
      this.handleFilterTrigger,
    )
    this.listeners.add(
      this.els['filter-trigger']?.element,
      'click',
      this.handleFilterTrigger,
    )

    this.els.filters.elements.forEach((filtersEl) => {
      this.listeners.add(filtersEl, 'change', this.handleFilterChange)
    })

    if (this.els?.['active-tags']?.element) {
      this.listeners.add(
        this.els['active-tags'].element,
        'click',
        this.handleActiveTagClick,
      )
    }

    this.listeners.add(document, 'click', this.handleGlobalClick)

    this.listeners.add(
      this.els.sorting.element,
      'change',
      this.handleSortChange,
    )

    window.onTagalysReady(this.handleInitialise)
  }

  handleInitialise () {
    if (this._initialized) return
    this._initialized = true

    const searchQuery = this.getSearchQueryFromUrl()
    const paginationContainer = this.els?.['pagination-load-more']?.element
    const productsContainer = this.els.products.element

    if (!searchQuery) {
      if (productsContainer) productsContainer.innerHTML = ''
      return
    }

    this.targetPageFromUrl = this.getPageFromUrl()
    this.isRestoringFromUrl = this.targetPageFromUrl > 1

    // eslint-disable-next-line no-undef
    window.searchResultsInstance = Tagalys.APIConnector.SearchResults.new({
      params: { query: searchQuery, perPage: this.itemsPerPage },
      onSuccess: async (response, helpers) => {
        const redirectFromHelpers =
          typeof helpers.getRedirectURL === 'function'
            ? helpers.getRedirectURL()
            : null

        const redirectFromMetadata = response?.metadata?.redirect_to_url
        const redirectUrl = redirectFromHelpers || redirectFromMetadata

        if (redirectUrl) {
          try {
            const absoluteRedirect = new URL(
              redirectUrl,
              window.location.origin,
            ).toString()
            const currentUrl =
              window.location.origin +
              window.location.pathname +
              window.location.search

            if (absoluteRedirect !== currentUrl) {
              window.location.href = absoluteRedirect
              return
            }
          } catch (error) {
            console.error(
              '[Tagalys] invalid redirect URL:',
              redirectUrl,
              error,
            )
          }
        }

        this.buildFilters(response, helpers)
        this.buildActiveFiltersFromApplied(response, helpers)

        const page = response?.page ?? 1
        const totalPages = response?.total_pages ?? 1
        const total = response?.total ?? 0
        const products = response?.products ?? []

        this.currentPage = page
        this.totalPages = totalPages
        this.totalResultsCount = total

        this.setFilterTotal()
        this.buildSortSelect(response, helpers)

        const clampedTarget = Math.min(
          Math.max(1, this.targetPageFromUrl),
          this.totalPages,
        )

        if (this.currentPage === 1) this.buildHeader(response)

        const shouldAppend = this.currentPage > 1
        const newCount = await this.renderProductsHtml(products, shouldAppend)

        if (paginationContainer) {
          paginationContainer.hidden = this.totalResultsCount === 0
        }

        this.renderedResultsCount = shouldAppend
          ? this.renderedResultsCount + newCount
          : newCount
        this.updatePaginationMeta()

        this.restoreFocus()

        if (this.isRestoringFromUrl) {
          if (this.currentPage < clampedTarget) {
            window.searchResultsInstance.helpers.goToNextPage()
            return
          }
          this.isRestoringFromUrl = false
          this.setPageInUrl(this.currentPage, { replace: true })
        } else {
          this.setPageInUrl(this.currentPage, { replace: true })
        }

        this.isLoadingPage = false
        this.updateLoadMoreButton()
      },
      onFailure: (response, helpers) => {
        console.error('[Tagalys] onFailure', { response, helpers })

        this.isLoadingPage = false
        this.isRestoringFromUrl = false
        this.updateLoadMoreButton()
      },
    })

    window.searchResultsInstance.helpers.call()
  }

  handleLoadMoreClick () {
    if (this.isLoadingPage) return
    if (this.currentPage >= this.totalPages) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    window.searchResultsInstance.helpers.goToNextPage()
  }

  async handleFilterTrigger () {
    const currentView = this.dataset.filterView

    const newView = currentView === 'opened' ? 'closed' : 'opened'

    this.updateView(currentView, newView)
  }

  handleFilterChange (event) {
    if (document.activeElement?.id) {
      this.dataset.restoreFocusId = document.activeElement.id
    }

    const input = event.target

    if (!input) return
    if (input.type !== 'checkbox') return

    const groupId = input.getAttribute('data-filter-id')
    const value = input.getAttribute('data-filter-value')

    if (!groupId) return

    const helpers = window.searchResultsInstance.helpers
    if (!helpers) return

    input.disabled = true
    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (input.checked) {
        helpers.applyFilter(groupId, value)
        helpers.call({ page: 1 })
      } else if (!input.checked) {
        helpers.clearFilter(groupId, value)
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] filter toggle failed', event)
    } finally {
      input.disabled = false
    }
  }

  handleSortChange = (event) => {
    const select = event.target.closest('select[data-tagalys-sort]')
    if (!select) return
    const sortId = select.value
    const helpers = window.searchResultsInstance.helpers
    if (!helpers || !sortId) return

    select.disabled = true
    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (helpers.applySortOption) {
        helpers.applySortOption(sortId)
        helpers.call({ page: 1 })
      } else {
        const params = {
          ...(window.searchResultsInstance.params || {}),
          sort: sortId,
          page: 1,
        }
        window.searchResultsInstance.params = params
        helpers.call(params)
      }
      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } finally {
      select.disabled = false
    }
  }

  updateLoadMoreButton () {
    const loadMoreButton = this.els?.['load-more']?.element
    if (!loadMoreButton) return

    const hasMorePages = this.currentPage < this.totalPages

    loadMoreButton.hidden = !hasMorePages
    loadMoreButton.disabled =
      this.isLoadingPage || this.isRestoringFromUrl || !hasMorePages

    const labelLoading = this.translations?.search?.pagination?.loading
    const labelMore = this.translations?.search?.pagination?.loadMore

    loadMoreButton.textContent =
      this.isLoadingPage || this.isRestoringFromUrl ? labelLoading : labelMore
    if (this.isLoadingPage || this.isRestoringFromUrl) {
      loadMoreButton.setAttribute('aria-busy', 'true')
    } else {
      loadMoreButton.removeAttribute('aria-busy')
    }
  }

  updatePaginationMeta () {
    const paginationMetaElement = this.els?.['pagination-meta']?.element
    if (!paginationMetaElement) return

    const showingCount = this.renderedResultsCount
    const totalCount = this.totalResultsCount

    const template = this.translations?.search?.pagination?.resultsAmount

    paginationMetaElement.innerHTML = translateString(template, {
      count: String(showingCount),
      total: String(totalCount),
    })
  }

  buildHeader (response) {
    const headerTermElement = this.els?.['header-term']?.element
    if (!headerTermElement) return

    const resultsTotal = response?.total ?? 0
    const resultsTerm = response?.query ?? this.getSearchQueryFromUrl()

    if (resultsTotal > 0) {
      const template =
        resultsTotal === 1
          ? this.translations?.search?.resultsWithCount?.one
          : this.translations?.search?.resultsWithCount?.other

      headerTermElement.innerHTML = translateString(template, {
        count: String(resultsTotal),
        terms: String(resultsTerm),
      })
    } else {
      headerTermElement.innerHTML = translateString(
        this.translations?.search?.noResults,
        {
          terms: String(resultsTerm),
        },
      )
    }
  }

  async updateView (currentView, newView) {
    const listClass = `pg-Search`

    await window.BAO.Cart.updateCartAttributes({
      current_style: newView,
    })

    this.classList.remove(`${listClass}-${currentView}Filters`)
    this.classList.add(`${listClass}-${newView}Filters`)

    this.setAttribute(`data-filter-view`, newView)

    this.els['header-trigger']?.element.setAttribute(
      'aria-hidden',
      newView === 'opened',
    )
  }

  setFilterTotal () {
    const total = this.totalResultsCount ?? 0
    const template =
      total === 1
        ? this.translations?.search?.filters?.totalCount.one
        : this.translations?.search?.filters?.totalCount.other

    this.els['trigger-total']?.elements.forEach((triggerTotalEl) => {
      triggerTotalEl.innerHTML = translateString(template, {
        count: total,
      })
    })

    this.els['drawer-total'].element.innerHTML = translateString(template, {
      count: total,
    })
  }

  buildFilters (response, helpers) {
    const filterGroups = helpers.getFilters()

    const containers = Array.from(this.els?.filters?.elements || [])
    const preOpenIdsPerContainer = containers.map((container) =>
      this.getOpenFacetIdsFromContainer(container),
    )

    const selectedGroupNames = new Set(
      (filterGroups || [])
        .filter(
          (filterGroup) =>
            Array.isArray(filterGroup.items) &&
            filterGroup.items.some((item) => !!item.selected),
        )
        .map((filterGroup) => filterGroup.name),
    )

    const render = (suffix = '') =>
      (filterGroups || [])
        .map((filterGroup) => {
          if (filterGroup.type === 'range') return []

          const groupName = filterGroup.name
          const groupId = filterGroup.id
          const itemsHtml = (filterGroup.items || [])
            .map((filterItem) => {
              const id = filterItem.id
              const name = filterItem.name
              const count = filterItem.count
              const selected = !!filterItem.selected
              const inputId = `${groupId}-${id}${suffix ? `-${suffix}` : ''}`
              const inputName = groupId
              const inputValue = String(id)

              return `
                <li class="clc-ProductGridFacet_Item" data-filter-group="${groupId}">
                  <label class="clc-ProductGridFacet_Label fz-13_143" for="${inputId}">
                    <input
                      class="clc-ProductGridFacet_Checkbox"
                      type="checkbox"
                      name="${inputName}"
                      value="${inputValue}"
                      id="${inputId}"
                      data-filter-id="${groupId}"
                      data-filter-value="${inputValue}"
                      ${selected ? 'checked' : ''}
                    >
                    <span class="clc-ProductGridFacet_CheckboxIcon">
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 10">
                        <path fill="currentColor" d="M9.722 1.208 5.8 5.128 9.692 9.02l-.707.708-3.892-3.893-3.857 3.856-.707-.707L4.386 5.13.5 1.243l.707-.707 3.886 3.885L9.015.5l.707.708Z"></path>
                      </svg>
                    </span>

                    ${name}

                    <span class="clc-ProductGridFacet_CheckboxCount">(${count})</span>
                  </label>
                </li>`
            })
            .join('')

          return `
            <div class="clc-ProductGridFacets_Item"
              data-accordion-group-el="item"
              aria-expanded="false"
              role="treeitem"
            >
              <button class="clc-ProductGridFacet_Header fz-13_143"
                data-accordion-group-el="trigger"
                aria-controls="${groupName}${suffix ? `-${suffix}` : ''}"
              >
                ${groupName}

                <span class="clc-ProductGridFacets_Icon">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 11 11">
                    <path fill="currentColor" d="M6 5.006h5v1H6v5H5v-5H0v-1h5v-5h1v5Z"></path>
                  </svg>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 11 12">
                    <path fill="currentColor" d="M11 6.512H0v-1h11v1Z"></path>
                  </svg>
                </span>
              </button>
              <div
                class="clc-ProductGridFacet_Body"
                data-accordion-group-el="content"
                aria-hidden="true" id="${groupName}${suffix ? `-${suffix}` : ''}"
              >
                <div class="clc-ProductGridFacet_Content">
                  <ul class="clc-ProductGridFacet_List">${itemsHtml}</ul>
                </div>
              </div>
            </div>`
        })
        .join('')

    this.els?.filters?.elements.forEach((containerEl, index) => {
      const suffix = String(index + 1)
      const html = render(suffix)
      containerEl.innerHTML = html

      const prevOpenIds = preOpenIdsPerContainer[index] || []

      const autoOpenIds = Array.from(selectedGroupNames).map(
        (name) => `${name}${suffix ? `-${suffix}` : ''}`,
      )

      const toOpen = Array.from(new Set([...prevOpenIds, ...autoOpenIds]))
      this.restoreFacetOpenState(containerEl, toOpen)
    })
  }

  buildActiveFiltersFromApplied (response, helpers) {
    const containers = Array.from(
      this.querySelectorAll('[data-tagalys-search-el="active-tags"]'),
    )
    if (!containers.length) return

    const groups = helpers.getAppliedFilters() || []
    const active = []

    groups.forEach((group) => {
      const groupId = group?.id
      const groupName = group?.name ?? groupId
      const items = Array.isArray(group?.items) ? group.items : []
      items.forEach((item) => {
        if (item?.selected === undefined || item.selected) {
          const value = String(item?.id ?? item?.value ?? '')
          const label = item?.name ?? item?.label ?? value
          if (groupId && value) {
            active.push({ groupId, groupName, value, label })
          }
        }
      })
    })

    const itemsHtml = active
      .map(
        ({ groupId, groupName, value, label }) => `
    <li class="clc-ActiveTag_Item">
      <button
        type="button"
        class="clc-ProductGridFacets_ActiveLink fz-12_130"
        data-active-tag
        data-filter-id="${groupId}"
        data-filter-value="${value}"
        aria-label="Remove ${groupName}: ${label}"
      >
        <span class="clc-ActiveTag_Text">${label}</span>
        
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 10 10">
          <path fill="currentColor" d="M9.722 1.208 5.8 5.128 9.692 9.02l-.707.708-3.892-3.893-3.857 3.856-.707-.707L4.386 5.13.5 1.243l.707-.707 3.886 3.885L9.015.5l.707.708Z"></path>
        </svg>
      </button>
    </li>
  `,
      )
      .join('')

    const clearAllHtml = active.length
      ? `
        <li class="clc-ActiveTag_Item">
          <button
            type="button"
            class="clc-ProductGridFacets_ActiveLink fz-12_130"
            data-active-tag-clear-all
          >
            ${this.translations?.search?.filters?.clearAll}
          </button>
        </li>
      `
      : ''

    const html = active.length ? itemsHtml + clearAllHtml : ''
    containers.forEach((ul) => {
      ul.innerHTML = html
    })
  }

  handleActiveTagClick (event) {
    const btn = event.target?.closest?.(
      '[data-active-tag], [data-active-tag-clear-all]',
    )
    if (!btn) return

    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (btn.hasAttribute('data-active-tag')) {
        const groupId = btn.getAttribute('data-filter-id')
        const value = btn.getAttribute('data-filter-value')

        if (!groupId) return

        helpers.clearFilter(groupId, value)
        helpers.call({ page: 1 })
      } else if (btn.hasAttribute('data-active-tag-clear-all')) {
        helpers.clearAllFilters()
        helpers.call({ page: 1 })
      }

      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] active tag action failed', event)
    }
  }

  handleDrawerClearClick () {
    const helpers = window.searchResultsInstance?.helpers
    if (!helpers) return

    this.isLoadingPage = true
    this.updateLoadMoreButton()

    try {
      if (helpers.clearAllFilters()) {
        helpers.clearAllFilters()
        helpers.call({ page: 1 })
      } else {
        helpers.call({ filters: {}, page: 1 })
      }
      this.currentPage = 0
      this.totalPages = 0
      this.renderedResultsCount = 0
      this.isRestoringFromUrl = false
      this.targetPageFromUrl = 1
    } catch (event) {
      console.error('[Tagalys] clear-all failed', event)
    }
  }

  handleGlobalClick = (event) => {
    const target = event.target

    const resetBtn = target.closest('[data-tagalys-search-el="drawer-clear"]')
    if (resetBtn) {
      event.preventDefault()
      this.handleDrawerClearClick()
      return
    }

    const tagBtn = target.closest(
      '[data-active-tag], [data-active-tag-clear-all]',
    )
    if (tagBtn && tagBtn.closest('[data-tagalys-search-el="active-tags"]')) {
      event.preventDefault()
      this.handleActiveTagClick(event)
    }
  }

  buildSortSelect (response, helpers) {
    const container = this.els.sorting.element
    if (!container) return

    const options = helpers.getSortOptions() || []
    const applied = helpers.getAppliedSortOption() || null

    const optionsHtml = options
      .map((option) => {
        const selected =
          option.selected || (applied && applied.id === option.id)
        return `<option value="${option.id}" ${selected ? 'selected' : ''}>${option.label}</option>`
      })
      .join('')

    const selectMarkup = `
      <label for="filters-sort" class="pg-Search_SortingLabel">
        Sort by:
      </label>
      <select id="filters-sort" class="pg-Search_SortSelect fz-13_143" data-tagalys-sort>
        ${optionsHtml}
      </select>
    `
    const existing = container.querySelector('select[data-tagalys-sort]')
    if (existing) {
      existing.innerHTML = optionsHtml
    } else {
      container.innerHTML = selectMarkup
    }
  }

  async renderProductsHtml (productsArray, appendInsteadOfReplace) {
    const productsContainerElement = this.els?.products?.element
    if (!productsContainerElement) return 0

    const htmlForNewCards = await this.createProductCardsHtml(productsArray)
    const newCount = (productsArray || []).length

    if (appendInsteadOfReplace) {
      productsContainerElement.insertAdjacentHTML('beforeend', htmlForNewCards)
    } else {
      productsContainerElement.innerHTML = htmlForNewCards
    }

    return newCount
  }

  async createProductCardsHtml (productsArray) {
    const productCardHtmlList = await Promise.all(
      (productsArray || []).map(async (product) => {
        const productUrl = `${window.Shopify.routes.root}products/${product.handle}`
        try {
          const sectionHtml = await window.BAO.sectionRenderer.fetchSingle(
            'tagalys-product-card',
            productUrl,
          )
          const sectionDocument =
            window.BAO.sectionRenderer.convertToDOM(sectionHtml)
          const articleElement = sectionDocument.querySelector('article')

          return articleElement
            ? `<div class="prd-List_Item">${articleElement.outerHTML}</div>`
            : ''
        } catch (error) {
          this.handleError?.(error)
          return ''
        }
      }),
    )

    return productCardHtmlList.join('')
  }

  getSearchQueryFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('q') ?? params.get('query') ?? ''
    return raw.replace(/\+/g, ' ').trim()
  }

  getPageFromUrl () {
    const params = new URLSearchParams(window.location.search)
    const page = parseInt(params.get('page') || '1', 10)
    return Number.isFinite(page) && page > 0 ? page : 1
  }

  setPageInUrl (pageNumber, { replace = true } = {}) {
    const url = new URL(window.location.href)
    if (pageNumber === 1) url.searchParams.delete('page')
    else url.searchParams.set('page', String(pageNumber))

    const method = replace ? 'replaceState' : 'pushState'
    window.history[method](null, '', url.toString())
  }

  getOpenFacetIdsFromContainer (container) {
    const openIds = []

    container
      .querySelectorAll('[data-accordion-group-el="item"]')
      .forEach((item) => {
        const isOpen = item.getAttribute('aria-expanded') === 'true'
        if (!isOpen) return

        const panel = item.querySelector(
          '[data-accordion-group-el="content"][id]',
        )
        const id = panel?.id
        if (id) openIds.push(id)
      })

    return openIds
  }

  restoreFacetOpenState (container, openIds) {
    const openSet = new Set(openIds)

    container
      .querySelectorAll('[data-accordion-group-el="item"]')
      .forEach((item) => {
        const panel = item.querySelector(
          '[data-accordion-group-el="content"][id]',
        )
        const id = panel?.id
        const isOpen = id ? openSet.has(id) : false

        item.setAttribute('aria-expanded', String(isOpen))

        if (panel) panel.setAttribute('aria-hidden', String(!isOpen))
      })
  }

  restoreFocus () {
    const restoreId = this.dataset.restoreFocusId
    if (!restoreId) return

    const el = this.querySelector(`#${CSS.escape(restoreId)}`)
    if (el) el.focus()

    delete this.dataset.restoreFocusId
  }

  get translations () {
    return window.theme?.strings ?? {}
  }
}

if (!customElements.get('tagalys-search')) {
  customElements.define('tagalys-search', TagalysSearch)
}
